import {
    useContext,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    View
} from "react-native";
import type IRadialChartProps from "./type";
import type {
    RadialChartGeometry,
    RadialChartBandArc,
    RadialChartVariant,
    RadialChartLayer,
    RadialChartItem
} from "./type";
import stylesheet, {
    RADIAL_CHART_REMAINING_KEY,
    RADIAL_CHART_CATEGORY_KEY,
    RADIAL_CHART_CENTER_SHIFT,
    RADIAL_CHART_GAUGE_START,
    RADIAL_CHART_DEFAULT_MAX,
    RADIAL_CHART_GAUGE_SPAN,
    RADIAL_CHART_VALUE_KEY,
    getRadialChartMeasures,
    useStyles
} from "./stylesheet";
import {
    getRadialChartGeometry,
    getRadialChartStatus,
    getRadialChartCenter,
    getRadialChartPoint,
    getRadialChartIndex,
    getRadialChartBands
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import Chart, {
    getChartStaggerProgress,
    useChartValueTransition,
    getChartTextWidth,
    getChartArcPath,
    useChartSeries,
    useChartModel
} from "../chart";
import type {
    ChartTooltipContent,
    ChartPlotLayout,
    ChartLegendItem,
    ChartDataRow
} from "../chart/type";
import {
    TriangleAlert as TriangleAlertIcon,
    OctagonAlert as OctagonAlertIcon,
    CircleCheck as CircleCheckIcon
} from "lucide-react-native";
import {
    Text as SvgText,
    Path,
    G
} from "react-native-svg";
import {
    formatPercent,
    formatNumber
} from "../../helpers/locale";
import {
    ChartContext
} from "../chart/context";
import Badge from "../badge";

const RADIAL_CHART_STATUS_ICONS = {
    warning: TriangleAlertIcon,
    success: CircleCheckIcon,
    danger: OctagonAlertIcon
};

const RadialChart: FC<IRadialChartProps> = ({
    categoryKey = RADIAL_CHART_CATEGORY_KEY,
    customLocalize: customLocalizeProp,
    valueFormatter: valueFormatterProp,
    valueKey = RADIAL_CHART_VALUE_KEY,
    customTheme: customThemeProp,
    maxValue: maxValueProp,
    variant = "progress",
    plotSize = "medium",
    isAnimatedOnMount,
    tooltipBehaviour,
    onActiveChange,
    categoryTitle,
    renderTooltip,
    centerTitle,
    centerValue,
    summary,
    bands,
    style,
    value,
    data
}) => {
    const context = useContext(ChartContext);

    const customTheme = customThemeProp ?? context?.customTheme;

    const customLocalize = customLocalizeProp ?? context?.customLocalize;

    const {
        shapeConfigs,
        typography,
        durations,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const isSingle = variant === "progress" || variant === "gauge";

    const valueFormatter = valueFormatterProp ?? ((current: number) => formatNumber({
        options: {
            maximumFractionDigits: 2
        },
        locale: activeLocale,
        value: current
    }));

    const {
        root: rootDynamicStyle,
        label: labelColor,
        track: trackColor,
        value: valueColor,
        ...statusColors
    } = useStyles({
        colors,
        spaces
    });

    const measures = getRadialChartMeasures({
        shapeConfigs,
        typography,
        borders,
        spaces
    });

    const sources = useMemo((): Array<{ value: number; key: string }> => {
        if(isSingle) {
            return value === undefined || !Number.isFinite(value) ? [] : [
                {
                    value: Math.max(0, value),
                    key: valueKey
                }
            ];
        }

        const rows = (data ?? []).map((row) => ({
            value: Math.max(0, Number(row[valueKey])),
            key: String(row[categoryKey] ?? "")
        })).filter((row) => Number.isFinite(row.value));

        return variant === "bars" ? rows.sort((first, second) => second.value - first.value) : rows;
    }, [
        categoryKey,
        isSingle,
        valueKey,
        variant,
        value,
        data
    ]);

    const keys = sources.map((source) => source.key);

    const {
        series
    } = useChartSeries({
        seriesKeys: keys
    });

    const items = sources.map((source, index): RadialChartItem => {
        const current = series[index];

        const title = isSingle ? context?.config[source.key]?.title ?? context?.title ?? source.key : current?.title ?? source.key;

        return {
            color: current?.color ?? colors.chart.categorical[1],
            colorIndex: current?.colorIndex ?? 1,
            value: source.value,
            key: source.key,
            title
        };
    });

    const opacities = series.map((item) => item.opacity);

    const visibleValues = items.filter((_, index) => !series[index]?.isHidden).map((item) => item.value);

    const maxValue = maxValueProp ?? (isSingle ? RADIAL_CHART_DEFAULT_MAX : Math.max(0, ...visibleValues));

    const shapeKey = `${variant}|${keys.join(",")}`;

    const transition = useChartValueTransition({
        values: [
            ...items.map((item) => item.value),
            maxValue
        ],
        customTheme,
        shapeKey
    });

    const layerRef = useRef<{ previous?: Pick<RadialChartLayer, "items" | "variant" | "opacities">; current: Pick<RadialChartLayer, "items" | "variant" | "opacities">; key: string }>({
        current: {
            opacities,
            variant,
            items
        },
        key: shapeKey
    });

    if(layerRef.current.key !== shapeKey) {
        layerRef.current = {
            previous: layerRef.current.current,
            current: {
                opacities,
                variant,
                items
            },
            key: shapeKey
        };
    } else {
        layerRef.current.current = {
            opacities,
            variant,
            items
        };
    }

    const currentLayer: RadialChartLayer = {
        values: transition.values.slice(0, items.length),
        maxValue: transition.values[items.length] ?? 0,
        opacity: transition.opacity,
        opacities,
        variant,
        items
    };

    const previousMeta = layerRef.current.previous;

    const previousLayer: RadialChartLayer | undefined = transition.previous && previousMeta && transition.previous.values.length === previousMeta.items.length + 1 ? {
        values: transition.previous.values.slice(0, previousMeta.items.length),
        maxValue: transition.previous.values[previousMeta.items.length] ?? 0,
        opacity: transition.previous.opacity,
        opacities: previousMeta.opacities,
        variant: previousMeta.variant,
        items: previousMeta.items
    } : undefined;

    const bandArcs = useMemo((): Array<RadialChartBandArc> => variant === "gauge" && bands?.length ? getRadialChartBands({
        max: maxValue,
        bands
    }) : [], [
        maxValue,
        variant,
        bands
    ]);

    const boundaryArcs = bandArcs.filter((arc) => arc.endRatio < 1);

    const getTickText = (ratio: number): string => valueFormatter(ratio * maxValue);

    const tickWidth = Math.max(0, ...boundaryArcs.map((arc) => getChartTextWidth({
        fontSize: measures.labelTypography.fontSize,
        text: getTickText(arc.endRatio)
    })), getChartTextWidth({
        fontSize: measures.labelTypography.fontSize,
        text: valueFormatter(maxValue)
    }));

    const getRatio = (current: number, max: number): number => max > 0 ? Math.min(1, Math.max(0, current / max)) : 0;

    const getPercent = (ratio: number): string => formatPercent({
        options: {
            maximumFractionDigits: 1
        },
        locale: activeLocale,
        value: ratio
    });

    const getCenterPercent = (ratio: number): string => formatPercent({
        options: {
            maximumFractionDigits: 0
        },
        locale: activeLocale,
        value: ratio
    });

    const getGeometry = ({
        layerVariant = variant,
        count = items.length,
        height,
        width
    }: Pick<ChartPlotLayout, "height" | "width"> & { layerVariant?: RadialChartVariant; count?: number }): RadialChartGeometry => getRadialChartGeometry({
        hasBands: bandArcs.length > 0,
        count: Math.max(1, count),
        variant: layerVariant,
        tickWidth,
        measures,
        height,
        width
    });

    const status = variant === "gauge" && items[0] ? getRadialChartStatus({
        value: items[0].value,
        bands
    }) : undefined;

    const rows = useMemo((): Array<ChartDataRow> => {
        const base = sources.map((source) => ({
            [categoryKey]: source.key,
            [valueKey]: source.value
        }));

        if(variant === "progress" && sources[0]) {
            return [
                ...base,
                {
                    [valueKey]: Math.max(0, (maxValueProp ?? RADIAL_CHART_DEFAULT_MAX) - sources[0].value),
                    [categoryKey]: RADIAL_CHART_REMAINING_KEY
                }
            ];
        }

        return base;
    }, [
        maxValueProp,
        categoryKey,
        valueKey,
        variant,
        sources
    ]);

    const seriesKeys = useMemo(() => [
        valueKey
    ], [
        valueKey
    ]);

    const getItemTitle = (key: string): string => {
        if(key === RADIAL_CHART_REMAINING_KEY && variant === "progress") {
            return localize("chart-remaining");
        }

        return items.find((item) => item.key === key)?.title ?? key;
    };

    const legendItems: Array<ChartLegendItem> = variant === "stacked" ? items.map((item) => ({
        colorIndex: item.colorIndex,
        title: item.title,
        key: item.key
    })) : [];

    useChartModel({
        model: {
            categoryFormatter: (current) => getItemTitle(String(current)),
            seriesKeys: variant === "stacked" ? keys : seriesKeys,
            kind: "radial",
            valueFormatter,
            categoryTitle,
            categoryKey,
            legendItems,
            data: rows
        }
    });

    const getItemText = (index: number): string => {
        const item = items[index];

        if(!item) {
            return "";
        }

        const text = `${item.title} ${valueFormatter(item.value)} · ${getPercent(getRatio(item.value, maxValue))}`;

        if(variant === "gauge" && status) {
            return `${text}. ${localize("chart-status")} ${status.title}`;
        }

        return text;
    };

    const getTooltip = (index: number): ChartTooltipContent | undefined => {
        const item = items[index];

        if(!item) {
            return undefined;
        }

        const ratio = getRatio(item.value, maxValue);

        const tooltipRows: ChartTooltipContent["rows"] = [
            {
                value: variant === "gauge" ? valueFormatter(item.value) : `${valueFormatter(item.value)} · ${getPercent(ratio)}`,
                colorIndex: item.colorIndex,
                title: item.title,
                shape: "square",
                key: item.key
            }
        ];

        if(variant === "progress") {
            tooltipRows.push({
                value: valueFormatter(Math.max(0, maxValue - item.value)),
                title: localize("chart-remaining"),
                key: RADIAL_CHART_REMAINING_KEY,
                colorIndex: "muted",
                shape: "square"
            });
        }

        if(variant === "gauge" && status) {
            tooltipRows.push({
                key: RADIAL_CHART_REMAINING_KEY,
                title: localize("chart-status"),
                colorIndex: "muted",
                value: status.title,
                shape: "square"
            });
        }

        return {
            title: isSingle ? context?.title ?? "" : categoryTitle ?? "",
            rows: tooltipRows
        };
    };

    const currentSummary = summary ?? [
        context?.title,
        context?.description,
        isSingle ? getItemText(0) : items.map((_, index) => getItemText(index)).join("; ")
    ].filter(Boolean).join(". ");

    const getIndex: Parameters<typeof Chart.Plot>[0]["getIndex"] = (point, layout) => getRadialChartIndex({
        geometry: getGeometry(layout),
        isRTL: layout.isRTL,
        x: point.x,
        y: point.y,
        measures,
        variant
    });

    const getAnchor: Parameters<typeof Chart.Plot>[0]["getAnchor"] = (index, _seriesIndex, layout) => {
        const item = items[index];

        const geometry = getGeometry(layout);

        const ring = geometry.rings[index];

        if(!item || !ring) {
            return undefined;
        }

        return getRadialChartPoint({
            angle: ring.startAngle + ring.span * getRatio(item.value, maxValue),
            isRTL: layout.isRTL,
            radius: ring.radius,
            geometry
        });
    };

    const getProgress = (layout: ChartPlotLayout, index: number): number => getChartStaggerProgress({
        count: isSingle ? 1 : Math.max(1, items.length),
        progress: layout.entrance.progress,
        index: isSingle ? 0 : index,
        stagger: durations.stagger,
        duration: durations.long
    });

    const renderTracks = (geometry: RadialChartGeometry, layout: ChartPlotLayout) => geometry.rings.map((ring, index) => <Path
        d={getChartArcPath({
            endAngle: ring.startAngle + ring.span,
            startAngle: ring.startAngle,
            innerRadius: ring.radius,
            outerRadius: ring.radius,
            isRTL: layout.isRTL,
            cx: geometry.cx,
            cy: geometry.cy
        })}
        strokeWidth={ring.thickness}
        key={`track-${index}`}
        strokeLinecap="round"
        stroke={trackColor}
        fill="none"
    />);

    const renderArcs = ({
        isPrevious,
        geometry,
        layout,
        layer
    }: {
        geometry: RadialChartGeometry;
        layout: ChartPlotLayout;
        layer: RadialChartLayer;
        isPrevious: boolean;
    }) => layer.items.map((item, index) => {
        const ring = geometry.rings[index];

        if(!ring) {
            return null;
        }

        const ratio = getRatio(layer.values[index] ?? 0, layer.maxValue) * getProgress(layout, index);

        const isActive = !isPrevious && layout.activeIndex === index;

        const path = getChartArcPath({
            endAngle: ring.startAngle + ring.span * ratio,
            startAngle: ring.startAngle,
            innerRadius: ring.radius,
            outerRadius: ring.radius,
            isRTL: layout.isRTL,
            cx: geometry.cx,
            cy: geometry.cy
        });

        if(!path) {
            return null;
        }

        return <Path
            opacity={(isPrevious ? 1 : layer.opacities[index] ?? 1) * layer.opacity}
            strokeWidth={ring.thickness + (isActive ? measures.lift : 0)}
            key={`${isPrevious ? "previous" : "current"}-${item.key}`}
            strokeLinecap="round"
            stroke={item.color}
            fill="none"
            d={path}
        />;
    });

    const renderBands = (geometry: RadialChartGeometry, layout: ChartPlotLayout) => {
        const ring = geometry.rings[0];

        if(!ring || !bandArcs.length) {
            return null;
        }

        const halfGap = shapeConfigs.chartSegmentGap / 2 / Math.max(1, geometry.bandRadius);

        const opacity = getProgress(layout, 0);

        return bandArcs.map((arc, index) => <Path
            d={getChartArcPath({
                startAngle: RADIAL_CHART_GAUGE_START + RADIAL_CHART_GAUGE_SPAN * arc.startRatio + (arc.startRatio > 0 ? halfGap : 0),
                endAngle: RADIAL_CHART_GAUGE_START + RADIAL_CHART_GAUGE_SPAN * arc.endRatio - (arc.endRatio < 1 ? halfGap : 0),
                innerRadius: geometry.bandRadius,
                outerRadius: geometry.bandRadius,
                isRTL: layout.isRTL,
                cx: geometry.cx,
                cy: geometry.cy
            })}
            stroke={statusColors[arc.status]}
            strokeWidth={measures.bandWidth}
            key={`band-${index}`}
            opacity={opacity}
            fill="none"
        />);
    };

    const renderLabel = ({
        anchor,
        text,
        x,
        y
    }: {
        anchor: "middle" | "start" | "end";
        text: string;
        x: number;
        y: number;
    }) => <SvgText
        fontFamily={measures.labelTypography.fontFamily}
        fontWeight={measures.labelTypography.fontWeight}
        fontSize={measures.labelTypography.fontSize}
        key={`${text}-${x}-${y}`}
        textAnchor={anchor}
        fill={labelColor}
        x={x}
        y={y}
    >
        {text}
    </SvgText>;

    const renderGaugeLabels = (geometry: RadialChartGeometry, layout: ChartPlotLayout) => {
        const ring = geometry.rings[0];

        if(!ring) {
            return null;
        }

        const shift = measures.labelTypography.fontSize * RADIAL_CHART_CENTER_SHIFT;

        const endY = geometry.cy + ring.thickness / 2 + measures.gap + measures.labelTypography.lineHeight / 2 + shift;

        const start = getRadialChartPoint({
            angle: RADIAL_CHART_GAUGE_START,
            isRTL: layout.isRTL,
            radius: ring.radius,
            geometry
        });

        const end = getRadialChartPoint({
            angle: RADIAL_CHART_GAUGE_START + RADIAL_CHART_GAUGE_SPAN,
            isRTL: layout.isRTL,
            radius: ring.radius,
            geometry
        });

        const tickRadius = geometry.bandRadius + measures.bandWidth / 2 + measures.gap;

        return <G
            opacity={getProgress(layout, 0)}
            aria-hidden={true}
        >
            {renderLabel({
                text: valueFormatter(0),
                anchor: "middle",
                x: start.x,
                y: endY
            })}
            {renderLabel({
                text: valueFormatter(maxValue),
                anchor: "middle",
                x: end.x,
                y: endY
            })}
            {
                boundaryArcs.map((arc) => {
                    const point = getRadialChartPoint({
                        angle: RADIAL_CHART_GAUGE_START + RADIAL_CHART_GAUGE_SPAN * arc.endRatio,
                        isRTL: layout.isRTL,
                        radius: tickRadius,
                        geometry
                    });

                    const offset = (point.x - geometry.cx) / Math.max(1, tickRadius);

                    const lift = (point.y - geometry.cy) / Math.max(1, tickRadius) * measures.labelTypography.lineHeight / 2;

                    return renderLabel({
                        anchor: offset > 0.2 ? "start" : offset < -0.2 ? "end" : "middle",
                        text: getTickText(arc.endRatio),
                        y: point.y + shift + lift,
                        x: point.x
                    });
                })
            }
        </G>;
    };

    const renderBarLabels = (geometry: RadialChartGeometry, layout: ChartPlotLayout) => {
        if(variant !== "bars" || !geometry.isLabelVisible) {
            return null;
        }

        const {
            labelTypography
        } = measures;

        const shift = labelTypography.fontSize * RADIAL_CHART_CENTER_SHIFT;

        const maxChars = Math.max(1, Math.floor((geometry.cx - measures.margin - measures.gap) / (labelTypography.fontSize * 0.66)));

        return <G
            aria-hidden={true}
        >
            {
                items.map((item, index) => {
                    const ring = geometry.rings[index];

                    if(!ring) {
                        return null;
                    }

                    const valueText = valueFormatter(item.value);

                    const full = `${item.title} ${valueText}`;

                    const name = full.length > maxChars ? `${item.title.slice(0, Math.max(1, maxChars - valueText.length - 2))}…` : item.title;

                    const isActive = layout.activeIndex === index;

                    return <SvgText
                        opacity={(opacities[index] ?? 1) * getProgress(layout, index) * currentLayer.opacity}
                        x={geometry.cx + (layout.isRTL ? 1 : -1) * measures.gap * 2}
                        textAnchor={layout.isRTL ? "start" : "end"}
                        fill={isActive ? valueColor : labelColor}
                        fontFamily={labelTypography.fontFamily}
                        fontWeight={labelTypography.fontWeight}
                        y={geometry.cy - ring.radius + shift}
                        fontSize={labelTypography.fontSize}
                        key={item.key}
                    >
                        {`${name} ${valueText}`}
                    </SvgText>;
                })
            }
        </G>;
    };

    const getCenterTarget = (): string | undefined => {
        if(centerValue !== undefined) {
            return centerValue;
        }

        const item = items[0];

        if(!item) {
            return undefined;
        }

        if(variant === "progress") {
            return getCenterPercent(getRatio(item.value, maxValue));
        }

        return variant === "gauge" ? valueFormatter(item.value) : undefined;
    };

    const renderCenter = (geometry: RadialChartGeometry, layout: ChartPlotLayout) => {
        const target = getCenterTarget();

        if(target === undefined || variant === "bars") {
            return null;
        }

        const center = getRadialChartCenter({
            title: centerTitle,
            value: target,
            geometry,
            measures,
            variant
        });

        if(!center) {
            return null;
        }

        const progress = getProgress(layout, 0);

        const itemRatio = getRatio(currentLayer.values[0] ?? 0, currentLayer.maxValue);

        const text = centerValue !== undefined ? centerValue : variant === "progress" ? getCenterPercent(itemRatio * progress) : valueFormatter((currentLayer.values[0] ?? 0) * progress);

        const {
            valueTypography,
            titleTypography
        } = center;

        return <G
            opacity={centerValue !== undefined ? progress * currentLayer.opacity : currentLayer.opacity}
            aria-hidden={true}
        >
            <SvgText
                y={center.top + valueTypography.lineHeight / 2 + valueTypography.fontSize * RADIAL_CHART_CENTER_SHIFT}
                fontFamily={valueTypography.fontFamily}
                fontWeight={valueTypography.fontWeight}
                fontSize={valueTypography.fontSize}
                textAnchor="middle"
                fill={valueColor}
                x={geometry.cx}
            >
                {text}
            </SvgText>
            {
                titleTypography && centerTitle ?
                    <SvgText
                        y={center.top + valueTypography.lineHeight + titleTypography.lineHeight / 2 + titleTypography.fontSize * RADIAL_CHART_CENTER_SHIFT}
                        fontFamily={titleTypography.fontFamily}
                        fontWeight={titleTypography.fontWeight}
                        fontSize={titleTypography.fontSize}
                        textAnchor="middle"
                        fill={labelColor}
                        x={geometry.cx}
                    >
                        {centerTitle}
                    </SvgText>
                :
                    null
            }
        </G>;
    };

    const renderPlot = (layout: ChartPlotLayout) => {
        const geometry = getGeometry(layout);

        const previousGeometry = previousLayer ? getGeometry({
            layerVariant: previousLayer.variant,
            count: previousLayer.items.length,
            height: layout.height,
            width: layout.width
        }) : undefined;

        return <>
            {renderBands(geometry, layout)}
            {renderTracks(geometry, layout)}
            {
                previousLayer && previousGeometry ?
                    renderArcs({
                        geometry: previousGeometry,
                        layer: previousLayer,
                        isPrevious: true,
                        layout
                    })
                :
                    null
            }
            {
                renderArcs({
                    layer: currentLayer,
                    isPrevious: false,
                    geometry,
                    layout
                })
            }
            {variant === "gauge" ? renderGaugeLabels(geometry, layout) : null}
            {renderBarLabels(geometry, layout)}
            {renderCenter(geometry, layout)}
        </>;
    };

    const renderStatus = () => {
        if(!status) {
            return null;
        }

        const StatusIcon = RADIAL_CHART_STATUS_ICONS[status.status];

        return <View
            style={stylesheet.chip}
        >
            <Badge
                accessibilityLabel={`${localize("chart-status")}: ${status.title}`}
                customTheme={customTheme}
                icon={({
                    customColor,
                    size
                }) => <StatusIcon
                    color={customColor}
                    size={size}
                />}
                type={status.status}
                iconPosition="start"
                title={status.title}
                variant="tonal"
                size="small"
                role="img"
            />
        </View>;
    };

    return <View
        style={[
            style,
            stylesheet.root,
            rootDynamicStyle
        ]}
    >
        <Chart.Plot
            isAnimatedOnMount={isAnimatedOnMount}
            tooltipBehaviour={tooltipBehaviour}
            onActiveChange={onActiveChange}
            customLocalize={customLocalize}
            renderTooltip={renderTooltip}
            style={stylesheet.container}
            pointCount={items.length}
            customTheme={customTheme}
            summary={currentSummary}
            getReading={getItemText}
            getTooltip={getTooltip}
            skeletonKind="radial"
            getAnchor={getAnchor}
            plotSize={plotSize}
            getIndex={getIndex}
        >
            {renderPlot}
        </Chart.Plot>
        {renderStatus()}
    </View>;
};
export default RadialChart;
